// frontend/src/pages/admin/AdminStudentAnalyticsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { adminService, AdminApiError } from '../../services/admin.service';
import { StudentListItem, StudentAnalytics } from '../../types/studentAnalytics.types';
import { StudentSelector } from '../../components/admin/analytics/StudentSelector';
import { TopicScoreList } from '../../components/admin/analytics/TopicScoreList';
import {
  Brain,
  Award,
  Flame,
  BarChart3,
  Sparkles,
  BookOpen,
  MousePointerClick,
  RotateCw,
  GraduationCap,
  Layers,
  Target,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import '../../styles/student-analytics.css';

export function AdminStudentAnalyticsPage() {
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<StudentAnalytics | null>(null);

  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [recommendation, setRecommendation] = useState<string | null>(null);
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'courses' | 'topics'>('all');

  useEffect(() => {
    adminService
      .listStudentsForAnalytics()
      .then(setStudents)
      .catch((err) => setError(err instanceof Error ? err.message : 'Öğrenciler alınamadı.'))
      .finally(() => setIsLoadingStudents(false));
  }, []);

  const loadAnalytics = useCallback((studentId: string) => {
    setSelectedId(studentId);
    setAnalytics(null);
    setRecommendation(null);
    setIsLoadingAnalytics(true);
    setError(null);

    adminService
      .getStudentAnalytics(studentId)
      .then(setAnalytics)
      .catch((err) => setError(err instanceof Error ? err.message : 'Analiz alınamadı.'))
      .finally(() => setIsLoadingAnalytics(false));
  }, []);

  async function handleGetRecommendation() {
    if (!analytics) return;
    setIsLoadingRecommendation(true);
    try {
      const rec = await adminService.getStudentAiRecommendation(
        analytics.profile.id,
        analytics.profile.full_name,
        analytics.strengths,
        analytics.weaknesses
      );
      setRecommendation(rec);
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Öneri oluşturulamadı.');
    } finally {
      setIsLoadingRecommendation(false);
    }
  }

  function getInitials(name: string): string {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  const SKILL_LEVEL_MAP: Record<string, string> = {
    beginner: 'Başlangıç Seviye',
    intermediate: 'Orta Seviye',
    advanced: 'İleri Seviye',
  };

  const MASTERY_BADGE: Record<string, { label: string; bg: string; color: string; icon: React.ReactNode }> = {
    master: { label: 'Usta (%85+)', bg: '#dcfce7', color: '#15803d', icon: <Award size={12} /> },
    proficient: { label: 'Yetkin (%70+)', bg: '#e0f2fe', color: '#0369a1', icon: <CheckCircle2 size={12} /> },
    learning: { label: 'Öğreniyor (%40+)', bg: '#fef3c7', color: '#b45309', icon: <BookOpen size={12} /> },
    beginner: { label: 'Başlangıç / Gelişmeli', bg: '#ffe4e6', color: '#be123c', icon: <AlertTriangle size={12} /> },
  };

  return (
    <div className="admin-analytics-page">
      <header className="analytics-page-header">
        <h1>
          <Brain color="#6366f1" size={32} /> AI Öğrenci Analitik & İlerleme Merkezi
        </h1>
        <p>Hem Ders (Kurs) hem de Konu (Ünite) bazında detaylı başarı ölçümü, ustalık haritası ve AI mentor tavsiyeleri</p>
      </header>

      <div className="analytics-dashboard-grid">
        {/* Sol Kolon: Öğrenci Seçici */}
        <div>
          {isLoadingStudents ? (
            <div className="courses-loading">Öğrenciler yükleniyor...</div>
          ) : (
            <StudentSelector students={students} selectedId={selectedId} onSelect={loadAnalytics} />
          )}
        </div>

        {/* Sağ Kolon: Analiz ve Ölçüm İçeriği */}
        <div>
          {error && <div className="quiz-error-banner" style={{ marginBottom: 16 }}>{error}</div>}

          {!selectedId && !isLoadingAnalytics && (
            <div className="analytics-empty-state-card">
              <div className="analytics-empty-icon">
                <MousePointerClick size={36} />
              </div>
              <h3>Öğrenci Seçin</h3>
              <p>
                Sol taraftaki listeden ders ve konu bazlı ilerleme durumunu incelemek istediğiniz öğrenciye tıklayın.
              </p>
            </div>
          )}

          {isLoadingAnalytics && (
            <div className="courses-loading" style={{ padding: 48, textAlign: 'center' }}>
              Öğrencinin ders ve konu bazlı detaylı raporu hazırlanıyor...
            </div>
          )}

          {analytics && !isLoadingAnalytics && (
            <div className="student-analytics-container">
              {/* Profil Header Banner */}
              <div className="student-profile-banner-card">
                <div className="student-banner-top">
                  <div className="student-banner-user">
                    <div className="student-banner-avatar">
                      {getInitials(analytics.profile.full_name)}
                    </div>
                    <div className="student-banner-name">
                      <h2>{analytics.profile.full_name}</h2>
                      <div className="student-level-tag">
                        <GraduationCap size={14} />
                        <span>{SKILL_LEVEL_MAP[analytics.profile.skill_level || 'beginner']}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="analytics-stats-grid">
                  <div className="stat-box-card">
                    <div className="stat-box-icon xp">
                      <Award size={20} />
                    </div>
                    <div>
                      <div className="stat-box-val">{analytics.profile.xp.toLocaleString('tr-TR')}</div>
                      <div className="stat-box-lbl">Toplam XP</div>
                    </div>
                  </div>

                  <div className="stat-box-card">
                    <div className="stat-box-icon streak">
                      <Flame size={20} />
                    </div>
                    <div>
                      <div className="stat-box-val">{analytics.profile.current_streak_days} Gün</div>
                      <div className="stat-box-lbl">Aktif Seri</div>
                    </div>
                  </div>

                  <div className="stat-box-card">
                    <div className="stat-box-icon quizzes">
                      <BookOpen size={20} />
                    </div>
                    <div>
                      <div className="stat-box-val">{analytics.totalLessonsCompleted || 0}</div>
                      <div className="stat-box-lbl">Tamamlanan Ders</div>
                    </div>
                  </div>

                  <div className="stat-box-card">
                    <div className="stat-box-icon badge">
                      <BarChart3 size={20} />
                    </div>
                    <div>
                      <div className="stat-box-val">{analytics.totalQuizzesAnalyzed || 0}</div>
                      <div className="stat-box-lbl">Quiz Analizi</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* İlerleme Sekme Filtresi */}
              <div className="analytics-tab-buttons" style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
                <button
                  type="button"
                  className={`lesson-btn ${activeTab === 'all' ? 'primary' : 'outline'} sm`}
                  onClick={() => setActiveTab('all')}
                >
                  <Layers size={14} /> Genel Özet
                </button>
                <button
                  type="button"
                  className={`lesson-btn ${activeTab === 'courses' ? 'primary' : 'outline'} sm`}
                  onClick={() => setActiveTab('courses')}
                >
                  <BookOpen size={14} /> Ders Bazlı İlerleme ({analytics.courseSummary?.length || 0})
                </button>
                <button
                  type="button"
                  className={`lesson-btn ${activeTab === 'topics' ? 'primary' : 'outline'} sm`}
                  onClick={() => setActiveTab('topics')}
                >
                  <Target size={14} /> Konu / Ünite Bazlı Ustalık ({analytics.topicDetails?.length || analytics.strengths.length + analytics.weaknesses.length})
                </button>
              </div>

              {/* DERS (KURS) BAZLI İLERLEME SEKSİYONU */}
              {(activeTab === 'all' || activeTab === 'courses') && (
                <div className="analytics-courses-progress-card" style={{ marginBottom: 20 }}>
                  <div className="analytics-section-title">
                    <BookOpen size={20} color="#6366f1" /> Ders (Kurs) İlerleme Durumu
                  </div>
                  {(!analytics.courseSummary || analytics.courseSummary.length === 0) ? (
                    <p style={{ color: '#64748b', fontSize: 14, fontStyle: 'italic', margin: 0 }}>
                      Öğrenci henüz bir derse kayıt olmadı.
                    </p>
                  ) : (
                    <div className="course-progress-list">
                      {analytics.courseSummary.map((course) => (
                        <div key={course.courseId || course.title} className="course-progress-item" style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 8, border: '1px solid #e2e8f0' }}>
                          <div className="course-progress-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>
                              <span>{course.title}</span>
                              <span style={{ fontSize: 12, marginLeft: 8, padding: '2px 8px', borderRadius: 12, background: course.status === 'completed' ? '#dcfce7' : '#e0f2fe', color: course.status === 'completed' ? '#15803d' : '#0369a1' }}>
                                {course.status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor'}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                              {course.completedLessons || 0} / {course.totalLessons || 1} Ders (%{course.progressPercent})
                            </div>
                          </div>
                          <div className="course-progress-track">
                            <div
                              className="course-progress-fill"
                              style={{ width: `${Math.min(100, Math.max(0, course.progressPercent))}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* KONU / ÜNİTE BAZLI USTA/BAŞARI İLERLEME SEKSİYONU */}
              {(activeTab === 'all' || activeTab === 'topics') && (
                <div className="analytics-courses-progress-card" style={{ marginBottom: 20, background: '#ffffff' }}>
                  <div className="analytics-section-title">
                    <Target size={20} color="#059669" /> Konu ve Ünite Bazlı Başarı & Ustalık Analizi
                  </div>
                  {(!analytics.topicDetails || analytics.topicDetails.length === 0) ? (
                    <div className="analytics-topics-grid">
                      <TopicScoreList
                        title="Güçlü Konular (%70+ Başarı)"
                        items={analytics.strengths}
                        emptyText="Henüz %70 üzeri başarı gösterilen bir konu yok."
                        variant="strength"
                      />
                      <TopicScoreList
                        title="Gelişim Alanları (Zayıf Konular)"
                        items={analytics.weaknesses}
                        emptyText="Belirgin bir zayıf konu tespit edilmedi."
                        variant="weakness"
                      />
                    </div>
                  ) : (
                    <div className="topic-mastery-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                      {analytics.topicDetails.map((topicItem) => {
                        const mBadge = MASTERY_BADGE[topicItem.masteryLevel || (topicItem.accuracy >= 85 ? 'master' : topicItem.accuracy >= 70 ? 'proficient' : topicItem.accuracy >= 40 ? 'learning' : 'beginner')];
                        return (
                          <div key={topicItem.topic} style={{ padding: 14, borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{topicItem.topic}</span>
                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600, background: mBadge.bg, color: mBadge.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {mBadge.icon} {mBadge.label}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                                Quiz Başarısı: <strong>%{topicItem.accuracy}</strong> ({topicItem.correct}/{topicItem.total} Doğru)
                              </div>
                              {topicItem.totalLessons !== undefined && (
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                  Konu Dersleri: <strong>{topicItem.completedLessons || 0}</strong> / {topicItem.totalLessons || 0} Tamamlandı
                                </div>
                              )}
                            </div>
                            <div style={{ marginTop: 10, height: 6, width: '100%', background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, topicItem.accuracy))}%`, background: topicItem.accuracy >= 70 ? '#10b981' : topicItem.accuracy >= 40 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
 
              {/* AI Mentor Rasyonel Rehberlik Kartı */}
              <div className="analytics-ai-recommendation-card">
                <div className="ai-recommendation-top">
                  <div className="ai-recommendation-title">
                    <Sparkles size={22} color="#7c3aed" /> AI Mentor Rasyonel Eğitmen Önerisi
                  </div>
                  <button
                    type="button"
                    className="lesson-btn primary sm"
                    onClick={handleGetRecommendation}
                    disabled={isLoadingRecommendation}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {isLoadingRecommendation ? (
                      'Hazırlanıyor...'
                    ) : recommendation ? (
                      <>
                        <RotateCw size={14} /> Yeniden Üret
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> Öneri Üret
                      </>
                    )}
                  </button>
                </div>

                <div className="ai-recommendation-body">
                  {recommendation ? (
                    <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{recommendation}</p>
                  ) : (
                    <p style={{ margin: 0, color: '#6b21a8', fontStyle: 'italic' }}>
                      Öğrencinin ders ve konu bazlı tüm verilerine dayanan disiplinli ve somut bir pedagojik çalışma planı oluşturmak için <strong>"Öneri Üret"</strong> butonuna tıklayın.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
